/**
 * Dashboard — home page, card-grid spotlight style (artifact 02).
 *
 * Layout (de arriba a abajo):
 *
 *   1. Header — saludo + LastUpdated chips (Balance, Hangar, Mining, Skills)
 *
 *   2. NextActions — TL;DR "¿qué necesita tu atención ahora?". Ya card-based.
 *
 *   3. CorpHeader — corp + alliance + roles.
 *
 *   4. EmpireAggregate — totales cross-char (multiboxer). Ya card-based.
 *
 *   5. Net Worth Hero — spotlight style:
 *      - 56px name (Space Grotesk): "Net worth"
 *      - 44px value (Space Grotesk accent)
 *      - tier-pill style tag "Estimated"
 *      - 4-col grid de StatTile mostrando wallet/hangar/skills/SP-count,
 *        cada una con mini-bar (% del total) y sub explicativo
 *
 *   6. MonthIskFlow — agregado 30d (income + mining − expense). Ya card.
 *
 *   7. DailyIskFlow — income de hoy. Ya card.
 *
 *   8. Quick stats strip — 4 StatTile (Balance, Hangar, Mining mes, Skill
 *      queue). Reemplaza StatCard inline.
 *
 *   9. ActiveJobsEta — top jobs activos. Ya card-based.
 *
 *  10. UpcomingEvents — próximos 3 eventos del calendario. Ya card-based.
 *
 *  11. RecentJournal — última 10 transacciones, convertida de tabla a
 *      card-list (cada tx es una card horizontal con date stamp + ref_type
 *      Pill + amount color-coded + description truncada).
 *
 *  12. WalletTimeline + UpcomingTimeline — gráficos al final.
 *
 * Caveats:
 *   - SP valuado a 1000 ISK/SP (regla de dedo, sub lo explica).
 *   - Mining del mes actual = suma entries del ledger con date startsWith YYYY-MM.
 *   - useAuth hook para el saludo (char name).
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { useAuth } from '../hooks/useAuth'
import { LastUpdated } from '../components/LastUpdated'
import { CorpHeader } from '../components/CorpHeader'
import { UpcomingTimeline } from '../components/UpcomingTimeline'
import { WalletTimeline } from '../components/WalletTimeline'
import { LoadingSkeleton } from '../components/LoadingSkeleton'
import { DailyIskFlow } from '../components/DailyIskFlow'
import { MonthIskFlow } from '../components/MonthIskFlow'
import { EmpireAggregate } from '../components/EmpireAggregate'
import { ActiveJobsEta } from '../components/ActiveJobsEta'
import { UpcomingEvents } from '../components/UpcomingEvents'
import { NextActions } from '../components/NextActions'
import { Pill, StatTile } from '../components/ui'

// Regla de dedo para valuación de SP — 1000 ISK/SP es optimista pero
// coherente con un extractor. Sub-text lo aclara.
const SP_TO_ISK = 1000

export function Dashboard() {
  const { data: me } = useAuth()
  const { data: wallet, isLoading: wl, dataUpdatedAt: wUpd } = useQuery({
    queryKey: ['wallet-balance'],
    queryFn: () => api.walletBalance(),
  })
  const { data: mining, isLoading: ml, dataUpdatedAt: mUpd } = useQuery({
    queryKey: ['mining-ledger'],
    queryFn: () => api.miningLedger(),
  })
  const { data: skills, isLoading: sl, dataUpdatedAt: sUpd } = useQuery({
    queryKey: ['skill-queue'],
    queryFn: () => api.skillQueue(),
  })
  const { data: inventory, isLoading: il, dataUpdatedAt: iUpd } = useQuery({
    queryKey: ['inventory-value'],
    queryFn: () => api.inventoryValue(),
  })
  // Skills list (lista completa, no queue) — para el net worth necesitamos
  // total_sp. Misma auth, mismo character, KV-cached.
  const { data: skillsList, dataUpdatedAt: skUpd } = useQuery({
    queryKey: ['skills-list'],
    queryFn: () => api.skills(),
  })

  // Net worth components.
  const totalSp = skillsList?.total_sp ?? 0
  const skillValue = totalSp * SP_TO_ISK
  const netWorth =
    (wallet?.balance ?? 0) + (inventory?.total_value ?? 0) + skillValue

  // Mining del mes actual.
  const now = useMemo(() => new Date(), [])
  const yyyyMM = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`
  const miningThisMonth =
    mining?.entries
      .filter((e) => e.date.startsWith(yyyyMM))
      .reduce((acc, e) => acc + e.quantity, 0) ?? 0

  // Próximo skill en la queue.
  const nextSkill = skills?.queue?.[0]
  const skillFinishesAt = nextSkill?.finish_date
    ? new Date(nextSkill.finish_date)
    : null

  // Para las mini-bars de net worth chunks (% del total).
  const walletPct =
    netWorth > 0 ? ((wallet?.balance ?? 0) / netWorth) * 100 : 0
  const inventoryPct =
    netWorth > 0 ? ((inventory?.total_value ?? 0) / netWorth) * 100 : 0
  const skillsPct = netWorth > 0 ? (skillValue / netWorth) * 100 : 0

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="headline text-3xl">Hola, {me?.character?.name}</h1>
        <p className="text-eve-muted text-sm italic mt-1">
          Resumen de tu actividad en New Eden.
        </p>
        <div className="mt-3 flex items-center gap-3 text-xs flex-wrap">
          <LastUpdated timestamp={wUpd} label="Balance" />
          <span className="text-eve-muted-2">·</span>
          <LastUpdated timestamp={iUpd} label="Hangar" />
          <span className="text-eve-muted-2">·</span>
          <LastUpdated timestamp={mUpd} label="Mining" />
          <span className="text-eve-muted-2">·</span>
          <LastUpdated timestamp={sUpd} label="Skills" />
        </div>
      </div>

      {/* Next actions — TL;DR "¿qué necesita tu atención ahora?". */}
      <NextActions />

      <CorpHeader />

      {/* Empire aggregate — totales cross-char (multiboxer). */}
      <EmpireAggregate />

      {/* Net worth hero — spotlight style (artifact 02). */}
      {(wallet || inventory || skillsList) && (
        <NetWorthHero
          netWorth={netWorth}
          wallet={wallet?.balance ?? null}
          inventory={inventory?.total_value ?? null}
          skills={skillValue}
          totalSp={totalSp}
          walletPct={walletPct}
          inventoryPct={inventoryPct}
          skillsPct={skillsPct}
        />
      )}

      {/* Month-to-date ISK flow — agregado 30d. */}
      <MonthIskFlow days={30} />

      {/* Daily ISK Flow — income de hoy. */}
      <DailyIskFlow />

      {/* Quick stats strip — 4 StatTile. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          variant="muted"
          size="sm"
          tone="accent"
          label="Balance"
          value={wallet ? formatISKShort(wallet.balance) : '—'}
          sub={wallet ? formatISK(wallet.balance, 0) + ' ISK' : undefined}
        />
        <StatTile
          variant="muted"
          size="sm"
          label="Tu hangar vale"
          value={inventory ? formatISKShort(inventory.total_value) : '—'}
          sub={
            inventory
              ? `${inventory.item_count.toLocaleString('en-US')} tipos · ${
                  inventory.unpriced_count + inventory.skipped_pricing > 0
                    ? `${inventory.unpriced_count + inventory.skipped_pricing} sin precio`
                    : 'todo priced'
                }`
              : 'cargando…'
          }
        />
        <StatTile
          variant="muted"
          size="sm"
          label="Mining del mes"
          value={miningThisMonth.toLocaleString('en-US')}
          sub={`unidades minadas en ${yyyyMM}`}
        />
        <StatTile
          variant="muted"
          size="sm"
          tone={nextSkill ? 'accent' : 'muted'}
          label="Skill en cola"
          value={nextSkill ? `L${nextSkill.finished_level}` : '—'}
          sub={
            nextSkill
              ? nextSkill.name
              : skillFinishesAt
                ? `Termina ${skillFinishesAt.toLocaleString()}`
                : 'Cola vacía'
          }
        />
      </div>

      {/* Active jobs ETA — top jobs activos. */}
      <ActiveJobsEta />

      {/* Upcoming events — próximos 3 eventos del calendario. */}
      <UpcomingEvents limit={3} />

      {/* Recent journal — card-list (reemplaza tabla densa). */}
      <RecentJournalSection />

      <WalletTimeline />
      <UpcomingTimeline />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * NetWorthHero — spotlight card (artifact 02). El number más grande de la
 * página, accent border + radial gradient overlay + tag pill. Abajo, 4-col
 * grid con StatTile para wallet / hangar / skills / un "balance total"
 * helper, cada uno con mini-bar (% del total).
 */
function NetWorthHero({
  netWorth,
  wallet,
  inventory,
  skills,
  totalSp,
  walletPct,
  inventoryPct,
  skillsPct,
}: {
  netWorth: number
  wallet: number | null
  inventory: number | null
  skills: number
  totalSp: number
  walletPct: number
  inventoryPct: number
  skillsPct: number
}) {
  return (
    <div className="bg-gradient-to-br from-eve-panel to-eve-bg border border-eve-accent/40 rounded-md p-5 sm:p-6 relative overflow-hidden shadow-gold">
      {/* Radial overlay (artifact 02 style) */}
      <div
        aria-hidden="true"
        className="absolute -top-1/2 -right-1/5 w-3/5 h-[200%] pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse, rgba(124, 58, 237, 0.08), transparent 70%)',
        }}
      />
      <div className="relative">
        {/* Hero header */}
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Pill tone="gold" size="md">
              Estimated
            </Pill>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-eve-text tracking-tight">
              Net worth
            </h2>
          </div>
          <span className="text-[10px] uppercase tracking-wider text-eve-muted-2 font-mono italic">
            wallet + hangar + skill points
          </span>
        </div>

        {/* Hero number */}
        <div className="mt-3 flex items-baseline gap-2 flex-wrap">
          <span className="font-display font-bold text-eve-accent text-3xl sm:text-4xl lg:text-5xl tracking-tight tabular-nums">
            {formatISK(netWorth, 0)}
          </span>
          <span className="text-base font-mono text-eve-muted">ISK</span>
        </div>

        {/* Chunks grid — 4 StatTile con mini-bars (% del total). */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <NetWorthChunk
            label="Wallet"
            value={wallet}
            pct={walletPct}
            sub={wallet !== null ? formatISK(wallet, 0) + ' ISK' : '—'}
          />
          <NetWorthChunk
            label="Hangar"
            value={inventory}
            pct={inventoryPct}
            sub={inventory !== null ? formatISK(inventory, 0) + ' ISK' : '—'}
          />
          <NetWorthChunk
            label="Skills"
            value={skills}
            pct={skillsPct}
            sub={`${totalSp.toLocaleString('en-US')} SP · @ ${SP_TO_ISK} ISK/SP`}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * NetWorthChunk — una stat individual dentro del NetWorthHero. Muestra
 * label, value (ISK short), mini-bar (% del total) y sub-text con el
 * valor exacto.
 */
function NetWorthChunk({
  label,
  value,
  pct,
  sub,
}: {
  label: string
  value: number | null
  pct: number
  sub: string
}) {
  const toneClass =
    value === null
      ? 'bg-eve-bg border border-eve-border'
      : 'bg-eve-panel/60 border border-eve-border/60'
  return (
    <div className={`rounded-md p-3 ${toneClass}`}>
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-wider text-eve-muted font-semibold">
          {label}
        </div>
        <div className="text-[10px] text-eve-muted font-mono tabular-nums">
          {pct.toFixed(0)}%
        </div>
      </div>
      <div className="mt-1 font-mono font-bold text-lg text-eve-text tabular-nums">
        {value !== null ? formatISKShort(value) : '—'}
      </div>
      <div className="mt-1.5 h-1 bg-eve-bg rounded-full overflow-hidden">
        <div
          className="h-full rounded-full bg-eve-accent/70"
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
      <div className="text-[10px] text-eve-muted mt-1 italic font-mono truncate" title={sub}>
        {sub}
      </div>
    </div>
  )
}

/**
 * RecentJournalSection — card-list con las últimas 10 transacciones del
 * journal. Reemplaza la tabla densa. Cada tx = card horizontal con date
 * stamp + ref_type Pill + amount (color-coded) + description truncada.
 */
function RecentJournalSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['wallet-journal', 1],
    queryFn: () => api.walletJournal(1),
  })

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-display text-lg font-semibold text-eve-text">
          Últimas transacciones
        </h2>
        <span className="text-[10px] text-eve-muted-2 italic">
          las 10 más recientes de tu wallet
        </span>
      </div>
      {isLoading ? (
        <LoadingSkeleton variant="default" />
      ) : !data?.entries?.length ? (
        <div className="bg-eve-panel border border-eve-border rounded-md p-6 text-eve-muted italic text-center">
          Sin transacciones recientes.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {data.entries.slice(0, 10).map((e) => (
            <JournalCard key={e.id} entry={e} />
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * JournalCard — una transacción individual. Card compacta con date stamp
 * (mono), ref_type Pill (corto), amount color-coded (positive green /
 * negative red), description truncada.
 */
function JournalCard({
  entry,
}: {
  entry: {
    id: number
    date: string
    ref_type: string
    amount: number
    description?: string
  }
}) {
  const positive = entry.amount >= 0
  const dateShort = useMemo(() => {
    const d = new Date(entry.date)
    const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
    const day = String(d.getUTCDate()).padStart(2, '0')
    const hh = String(d.getUTCHours()).padStart(2, '0')
    const mm = String(d.getUTCMinutes()).padStart(2, '0')
    return `${day} ${month} ${hh}:${mm}`
  }, [entry.date])

  return (
    <div className="bg-eve-panel border border-eve-border rounded-md p-3 hover:border-eve-accent/30 transition-colors">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="font-mono text-[10px] text-eve-muted shrink-0">
            {dateShort}
          </span>
          <Pill tone="muted" size="xs">
            {entry.ref_type}
          </Pill>
        </div>
        <div
          className={`font-mono font-bold tabular-nums text-sm shrink-0 ${
            positive ? 'text-eve-success' : 'text-eve-danger'
          }`}
        >
          {positive ? '+' : ''}
          {formatISKShort(entry.amount)}
          <span className="text-[10px] text-eve-muted ml-1 font-sans">
            ISK
          </span>
        </div>
      </div>
      {entry.description && (
        <div
          className="text-[11px] text-eve-muted mt-1.5 truncate font-mono"
          title={entry.description}
        >
          {entry.description}
        </div>
      )}
    </div>
  )
}
